import React, { useMemo, useState, useEffect, useContext } from "react";
import { Trans, t } from "@lingui/macro";
import PropTypes from "prop-types";
import { i18nMark } from "@lingui/react";

import { ReadonlyEditor, FORMAT_TYPES } from "widgets/richtext";
import { Classes } from "base/classes";
import { i18n } from "base/i18n";
import {
  UserIcon,
  AVATAR_SIZE,
  Icon,
  StatusChip,
  STATUS_CHIP_COLOR,
  STATUS_CHIP_TYPE,
  CHIP_SIZES
} from "widgets/datadisplay";
import {
  Tooltip,
  TOOLTIP_TYPES,
  TOOLTIP_POSITION,
  Alert,
  Toast
} from "widgets/feedback";
import {
  Time,
  TIME_FORMAT_TYPES,
  Button,
  BUTTON_TYPES,
  BUTTON_SIZES
} from "widgets/inputs";
import { Popover, POPOVER_POSITIONS } from "widgets/popover";
import { useUserStore } from "base/stores";
import { generateId } from "base/utils";

import { Embedurl } from "../post/embedurl";
import { CommentQuote } from "./comment.quote";
import { CommentBox } from "./comment.box";
import { ForumService } from "../../common/service/forum.service";
import { useForumStore } from "../../common/store/forum.store";
import { useChannelStore } from "@/common/store/channel.store";
import { DOWNVOTE, UPVOTE, CHANNEL_CONST } from "@/common/utils/constant";
import {
  voteDetails,
  getVoteCount,
  getEditedContent,
  isNoContent
} from "@/common/utils/utils";
import { CommentAttachments } from "./comment.attachments";
import { Mention } from "../post/mention";
import { SpaceContext } from "@/container/forum";

import styles from "../../forum/forum.css";

import UpvoteIcon from "../../common/icons/Upvote.svg";
import UpvotedIcon from "../../common/icons/Upvoted.svg";
import DownvoteIcon from "../../common/icons/Downvote.svg";
import DownvotedIcon from "../../common/icons/Downvoted.svg";
import ReplyIcon from "../../common/icons/Reply.svg";
import HorizontalMoreIcon from "../../icons/TopicOptions.svg";
import EditIcon from "../../common/icons/Edit.svg";
import DeleteIcon from "../../common/icons/Delete.svg";
import LinkIcon from "../../common/icons/Link.svg";
import CheckIcon from "../../common/icons/CircledTick.svg";
import MarkIcon from "../../common/icons/BestSolution.svg";
import CloseIcon from "../../common/icons/CircledClose.svg";

const { showConfirm } = Alert;
const { showSuccess } = Toast;
const SOLUTIONS = {
  true: i18nMark("Deselect the best solution"),
  false: i18nMark("Accept as the best solution")
};
export function CommentSection({
  comment,
  topicId,
  getParentComment,
  bestCommentId,
  index,
  setEditorFocus,
  isClosedTopic,
  highlightComment,
  commentId,
  teamId,
  isShowViewComments
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [reaction, setReactions] = useState([]);
  const [voteCount, setVoteCount] = useState(0);
  const { currentUser } = useUserStore.getState();
  const { topicData, isCurrentTopicDeleted } = useForumStore((state) => ({
    topicData: state.topicData[topicId] || {},
    isCurrentTopicDeleted: state.isCurrentTopicDeleted
  }));
  const { mediumId } = useChannelStore.getState();
  const [attachments, setAttachments] = useState({ images: [], files: [] });

  const { spaceRoute } = useContext(SpaceContext);

  const {
    commentContent,
    quoteContent,
    parentComment,
    isNoTextComment,
    parentAttachments,
    commentWithEditedText
  } = useMemo(
    function filterContents() {
      if (comment) {
        let isNoTextComment = false;
        let parentAttachments = {
          images: [],
          files: []
        };
        const commentContent = comment.Content.filter(
          (comment) =>
            ![FORMAT_TYPES.BLOCKQUOTE, FORMAT_TYPES.COLLAGE].includes(
              comment.type
            )
        );
        let quoteContent = comment.Content.find(
          (comment) => comment.type === FORMAT_TYPES.BLOCKQUOTE
        );
        let parentComment = getParentComment(comment.ParentCommentId);
        if (comment.ParentCommentId && !quoteContent) {
          quoteContent = parentComment?.Content;
        }
        if (!comment.ParentCommentId && quoteContent) {
          parentComment = getParentComment(quoteContent?.data?.EntityId);
        }
        if (quoteContent && !Array.isArray(quoteContent)) {
          quoteContent = [quoteContent];
        } else if (!quoteContent) {
          quoteContent = [];
        }
        if (commentContent) {
          isNoTextComment = isNoContent(commentContent);
        }
        if (parentComment && comment.ParentCommentId) {
          const collageData = parentComment.Content.find(
            (comment) => comment.type === FORMAT_TYPES.COLLAGE
          );
          if (collageData?.data)
            parentAttachments = {
              ...collageData.data
            };
        }
        return {
          commentContent,
          quoteContent,
          parentComment,
          isNoTextComment,
          parentAttachments,
          commentWithEditedText: getEditedContent(
            comment.IsEdited,
            commentContent
          )
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comment.Content, comment.ParentCommentId, comment.type, comment.IsEdited]
  );

  const isCreator = useMemo(
    function setCreator() {
      return comment?._created_by?._id === currentUser?._id;
    },
    [comment, currentUser]
  );

  const { isUpvoted, isDownvoted } = useMemo(
    function getVoteDetails() {
      return voteDetails(reaction);
    },
    [reaction]
  );

  useEffect(
    function setDetails() {
      const reactions = comment?.Reactions?.find(
        (reaction) => reaction._id === currentUser._id
      );
      setReactions(reactions);
    },
    [comment, currentUser._id]
  );

  const isBestSolution = useMemo(
    function setBestSolution() {
      return comment?._id === topicData?.BestSolution;
    },
    [comment, topicData]
  );

  const isDisableVote = useMemo(
    function enable() {
      return (
        isClosedTopic ||
        isCurrentTopicDeleted ||
        comment?._created_by?._id === currentUser._id
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isClosedTopic, isCurrentTopicDeleted, comment]
  );

  useEffect(
    function setCount() {
      setVoteCount(comment.VoteCount || 0);
    },
    [comment.VoteCount]
  );

  useEffect(
    function setAttachmentData() {
      const currentData = [...comment.Content];
      let collageData = currentData?.find((el) => {
        return el.type === FORMAT_TYPES.COLLAGE;
      });
      if (collageData) {
        setAttachments((prevState) => {
          return {
            ...prevState,
            images: collageData?.data.images,
            files: collageData?.data.files
          };
        });
      }
    },
    [comment.Content]
  );

  useEffect(
    function scrollView() {
      if (comment.isFromLiveSync) {
        const scrollElement = document.getElementById("topic-content");
        scrollElement.scrollTop =
          scrollElement.scrollHeight - scrollElement.clientHeight;
      }
    },
    [comment]
  );

  const plugin = useMemo(function setPlugins() {
    return {
      [FORMAT_TYPES.EMBED_LINK]: (prop) => {
        return (
          <Embedurl
            innerRef={{}}
            {...prop}
            node={prop.node.data}
            title={CHANNEL_CONST.COMMENT}
            readOnly={true}
          />
        );
      },
      [FORMAT_TYPES.MENTION]: (prop) => {
        return <Mention innerRef={{}} node={prop.node.data} teamId={teamId} />;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onDelete() {
    showConfirm({
      title: i18n._(t`Delete comment`),
      content: i18n._(t`Do you want to delete this comment?`),
      okText: i18n._(t`Delete`),
      cancelText: i18n._(t`Cancel`),
      onOK: () => {
        const [deletePromise] = ForumService.deleteComment(
          teamId,
          mediumId,
          topicData._id,
          comment._id
        );
        deletePromise().then(() => {
          useForumStore.getState().deleteComment(topicId, comment._id);
        });
      },
      okButtonType: BUTTON_TYPES.ERROR
    });
    setIsPopoverOpen(false);
  }

  function postVote(voteType) {
    if (!isDisableVote) {
      const eventId = generateId("Ev");
      useForumStore.getState().setEventId(eventId);
      const data = {
        ReactionType: voteType,
        EventId: eventId
      };
      const commentVoteCount = getVoteCount(
        isUpvoted,
        isDownvoted,
        voteType,
        voteCount
      );
      if (
        (isUpvoted && voteType === UPVOTE) ||
        (isDownvoted && voteType === DOWNVOTE)
      ) {
        const [removeVotePromise] = ForumService.removeCommentVote(
          teamId,
          mediumId,
          topicData._id,
          comment._id
        );
        removeVotePromise().catch((error) => {
          const reactions = comment?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        if (comment._id === topicData?.BestSolution) {
          useForumStore
            .getState()
            .setBestSolutionReactions(
              topicId,
              {},
              commentVoteCount,
              comment._id
            );
        } else {
          setReactions({});
        }
        const reactions = comment?.Reactions?.filter(
          (reactions) => reactions._id !== currentUser._id
        );
        useForumStore
          .getState()
          .updateCommentVote(topicId, comment._id, reactions);
      } else {
        const [postVotePromise] = ForumService.postCommentVote(
          teamId,
          mediumId,
          topicData._id,
          comment._id,
          data
        );
        postVotePromise().catch(() => {
          const reactions = comment?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        const reactions = {
          _id: currentUser._id,
          ReactionType: voteType
        };
        if (comment._id === topicData?.BestSolution) {
          useForumStore
            .getState()
            .setBestSolutionReactions(
              topicId,
              reactions,
              commentVoteCount,
              comment._id
            );
        } else {
          setReactions(reactions);
          useForumStore
            .getState()
            .updateCommentVote(topicId, comment._id, reactions);
        }
      }
      if (comment._id !== topicData?.BestSolution) {
        setVoteCount(commentVoteCount);
      }
      useForumStore
        .getState()
        .updateCommentVote(topicId, comment._id, null, commentVoteCount);
    }
  }

  function switchSolution() {
    const commentId = isBestSolution ? "" : comment._id;
    const data = {
      BestSolution: commentId
    };
    const [updateTopicPromise] = ForumService.updateTopic(
      teamId,
      mediumId,
      topicData._id,
      data
    );
    updateTopicPromise().then(() => {
      useForumStore.getState().markBestSolution(topicData._id, commentId);
    });
    setIsPopoverOpen(false);
  }

  function onReplyComments() {
    setEditorFocus(true);
    useForumStore.getState().setReplyComment({
      ...comment,
      Content: commentContent,
      attachments: attachments
    });
    useForumStore.getState().setQuotedText([]);
  }

  function onCopyComment(comment) {
    let hostName = window.location.host;
    navigator.clipboard.writeText(
      // eslint-disable-next-line max-len
      `https://${hostName}/view/${spaceRoute}/${teamId}/${topicData.Medium}/${topicData.MediumId}/topic/${topicData._id}/comment/${comment._id}`
    );
    showSuccess({
      message: i18n._(t`Link copied`)
    });
    setIsPopoverOpen(false);
  }

  return (
    <div
      id={comment._id}
      className={`${
        (highlightComment && commentId === comment._id) ||
        comment.isFromLiveSync
          ? styles.highlightComment
          : ""
      } ${styles.commentSection} ${
        bestCommentId === comment._id ? styles.bestSolutionContainer : ""
      }`}
    >
      <div className={styles.creatorSection}>
        <UserIcon
          size={AVATAR_SIZE.SIZE44}
          userId={comment?._created_by._id}
          name={comment?._created_by.Name}
        />
        <div className={styles.commentorName}>
          <div className={styles.df8}>
            <div className={"h6-600"}>{comment?._created_by.Name}</div>
            {isBestSolution &&
              (bestCommentId === comment._id ? (
                <StatusChip
                  size={CHIP_SIZES.SMALL}
                  color={STATUS_CHIP_COLOR.GREEN}
                  type={STATUS_CHIP_TYPE.FILLED}
                >
                  <Trans>Best solution</Trans>
                </StatusChip>
              ) : (
                <CheckIcon />
              ))}
          </div>

          <div className={`small ${styles.shortTime}`}>
            <Time
              date={comment._created_at}
              type={TIME_FORMAT_TYPES.RELATIVE_SHORT}
            />
          </div>
        </div>
      </div>
      <div className={`${styles.commentEditor}`} id={comment._id}>
        {(quoteContent.length > 0 || comment.ParentCommentId) && (
          <CommentQuote
            topicId={topicId}
            quoteContent={quoteContent}
            parentComment={parentComment}
            comment={comment}
            commentContent={commentContent}
            attachments={parentAttachments}
            isNoText={isNoContent(
              parentComment?.Content?.filter(
                (comment) => comment.type !== FORMAT_TYPES.COLLAGE
              )
            )}
            teamId={teamId}
          />
        )}
        {isEdit ? (
          <CommentBox
            focus={true}
            topicId={topicId}
            value={commentContent}
            isEdit={isEdit}
            commentId={comment._id}
            setEdit={setEdit}
            commentAttachments={attachments}
            setEditorFocus={setEditorFocus}
            teamId={teamId}
            comment={comment}
          />
        ) : (
          <React.Fragment>
            {!isNoTextComment && (
              <ReadonlyEditor
                value={commentWithEditedText}
                className={Classes.baseplus400}
                renderer={plugin}
              />
            )}
            <CommentAttachments attachments={attachments} readonly={true} />
          </React.Fragment>
        )}
        <div className={`${styles.actions} ${styles.flexBox}`}>
          <div className={styles.left}>
            <Tooltip
              content={<Trans>Upvote</Trans>}
              type={TOOLTIP_TYPES.SMALL}
              position={TOOLTIP_POSITION.TOP}
            >
              <Icon
                paddingNone={true}
                highlight={false}
                onClick={() => postVote(UPVOTE)}
              >
                {isUpvoted ? (
                  <UpvotedIcon />
                ) : (
                  <UpvoteIcon
                    className={`${styles.voteIcon} ${
                      isDisableVote ? styles.disableIcon : styles.up
                    }`}
                  />
                )}
              </Icon>
            </Tooltip>
            {voteCount || 0}
            <Tooltip
              content={<Trans>Downvote</Trans>}
              type={TOOLTIP_TYPES.SMALL}
              position={TOOLTIP_POSITION.TOP}
            >
              <Icon
                paddingNone={true}
                highlight={false}
                onClick={() => postVote(DOWNVOTE)}
              >
                {isDownvoted ? (
                  <DownvotedIcon />
                ) : (
                  <DownvoteIcon
                    className={`${styles.voteIcon} ${
                      isDisableVote ? styles.disableIcon : styles.down
                    }`}
                  />
                )}
              </Icon>
            </Tooltip>
          </div>
          <div className={`${styles.right} ${styles.replyGroup}`}>
            {!isClosedTopic && !isCurrentTopicDeleted && !isShowViewComments && (
              <Button
                type={BUTTON_TYPES.SECONDARY}
                size={BUTTON_SIZES.TINY}
                onClick={onReplyComments}
                className={styles.replyBtn}
              >
                <ReplyIcon className={styles.replyIcon} />
                <span>
                  <Trans>Reply</Trans>
                </span>
              </Button>
            )}
            {!isClosedTopic && !isCurrentTopicDeleted && (
              <Popover
                isOpen={isPopoverOpen}
                position={POPOVER_POSITIONS.BOTTOM_RIGHT}
                onOpen={() => setIsPopoverOpen(true)}
                onClose={() => {
                  setIsPopoverOpen(false);
                }}
                targetRenderer={() => {
                  return (
                    <div
                      className={`${styles.menuMoreIcon} ${
                        isPopoverOpen ? styles.menuIconFocus : ""
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HorizontalMoreIcon />
                    </div>
                  );
                }}
              >
                <div className={`${styles.moreMenuOption} `}>
                  {!isClosedTopic && !isCurrentTopicDeleted && (
                    <React.Fragment>
                      {isCreator && comment._id !== topicData?.BestSolution && (
                        <div
                          className={`base ${styles.item}`}
                          onClick={() => {
                            setEdit(true);
                            setIsPopoverOpen(false);
                          }}
                        >
                          <EditIcon />
                          <span>
                            <Trans>Edit</Trans>
                          </span>
                        </div>
                      )}
                      {topicData._created_by?._id === currentUser._id &&
                        topicData._created_by?._id !==
                          comment._created_by._id && (
                          <div
                            className={`base ${styles.item}`}
                            onClick={switchSolution}
                            data-heaplocator={
                              isBestSolution
                                ? "forum-topic-not-best-solution"
                                : "forum-topic-best-solution"
                            }
                          >
                            {isBestSolution ? <CloseIcon /> : <MarkIcon />}
                            <span>{i18n._(SOLUTIONS[isBestSolution])}</span>
                          </div>
                        )}
                    </React.Fragment>
                  )}
                  <div
                    className={`base ${styles.item}`}
                    onClick={() => onCopyComment(comment)}
                  >
                    <LinkIcon />
                    <span>
                      <Trans>Copy reply link</Trans>
                    </span>
                  </div>
                  {isCreator &&
                    !isClosedTopic &&
                    !isCurrentTopicDeleted &&
                    comment._id !== topicData?.BestSolution && (
                      <div
                        className={`base ${styles.item} ${styles.danger}`}
                        onClick={onDelete}
                      >
                        <DeleteIcon />
                        <span>
                          <Trans>Delete</Trans>
                        </span>
                      </div>
                    )}
                </div>
              </Popover>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

CommentSection.propTypes = {
  comment: PropTypes.object,
  topicId: PropTypes.string,
  getParentComment: PropTypes.func,
  bestCommentId: PropTypes.string,
  index: PropTypes.number,
  setEditorFocus: PropTypes.func,
  isClosedTopic: PropTypes.bool,
  highlightComment: PropTypes.bool,
  commentId: PropTypes.string,
  teamId: PropTypes.string,
  isShowViewComments: PropTypes.bool
};
